// Who holds the microphone (C-7 MicOwner, addendum A-3).
//
// One module singleton, so a meeting recording survives navigation and every
// surface sees the same owner.
//   - A live meeting refuses every claim, including a second meeting.
//   - A voice enrolment (BUILD 2) is exclusive in the same way: while it
//     records, every claim is refused.
//   - Dictation and voice exclude each other: a new interactive claim
//     pre-empts the current dictation or voice session (the latest tap wins);
//     dictation pre-empts dictation.
//   - A meeting or enrolment claim pre-empts a live dictation or voice
//     session, whose own policy then keeps its audio.
// `release` only releases while its claim is still the current one.
import type { MicKind, MicOwner } from '../contracts';

type Claim = { kind: MicKind; onPreempt: () => void; token: symbol };

/** Holders that refuse every other claim. */
// voice-structural: microphone holder kinds
const EXCLUSIVE: ReadonlySet<MicKind> = new Set(['meeting', 'enrolment']);

export function createMicOwner(): MicOwner {
  let active: Claim | null = null;
  const subscribers = new Set<(kind: MicKind | null) => void>();
  const publish = () => {
    const kind = active?.kind ?? null;
    for (const subscriber of [...subscribers]) subscriber(kind);
  };

  return {
    claim(kind, onPreempt) {
      if (active && EXCLUSIVE.has(active.kind)) return null;
      const previous = active;
      const claim: Claim = { kind, onPreempt, token: Symbol(kind) };
      active = claim;
      previous?.onPreempt();
      publish();
      return () => {
        if (active?.token !== claim.token) return;
        active = null;
        publish();
      };
    },
    current: () => active?.kind ?? null,
    subscribe(callback) {
      subscribers.add(callback);
      return () => {
        subscribers.delete(callback);
      };
    },
  };
}

/** The app-wide microphone owner. */
export const micOwner: MicOwner = createMicOwner();
